import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { hasPermission, PERMISSIONS } from '@helios/authz';
import { Button, ErrorState } from '@helios/ui';
import { useState, type FormEvent } from 'react';
import { useAuth } from '@/features/auth/authContext';
import {
  sessionScopeKey,
  useSessionScopeFence,
  type SessionScopeIdentity,
} from '@/hooks/useSessionScope';
import { aiAgentsApi } from '@/features/ai-agents/aiAgents.api';
import { aiAgentsQueryKeys } from '@/features/ai-agents/aiAgents.hooks';
import type { AgentSummary } from '@/features/ai-agents/aiAgents.types';
import { SettingsDraftActions } from './SettingsDraftActions';
import {
  SettingsField,
  SettingsLoadingState,
  SettingsMutationBanner,
  SettingsSection,
  settingsControlClass,
} from './settingsChrome';
import { changedSettingsFields, useSettingsDraft } from './useSettingsDraft';

export function AiAgentControls() {
  const fence = useSessionScopeFence(PERMISSIONS.AI_AGENTS_READ);
  const scope = JSON.stringify(sessionScopeKey(fence.identity));
  const [filters, setFilters] = useState<{ scope: string; search: string; page: number } | null>(
    null,
  );
  const current = filters?.scope === scope ? filters : { scope, search: '', page: 1 };
  const query = useQuery({
    queryKey: [
      ...aiAgentsQueryKeys.all,
      'settings',
      ...sessionScopeKey(fence.identity),
      current.search,
      current.page,
    ],
    enabled: fence.enabled,
    queryFn: async ({ signal }) => {
      const origin = fence.identity;
      if (!fence.isCurrent(origin)) throw new Error('Current agent access is required.');
      const result = await aiAgentsApi.listAgents(
        { page: current.page, pageSize: 50, search: current.search },
        { sessionOrigin: origin.sessionOrigin, allowRefresh: false, signal },
      );
      signal.throwIfAborted();
      if (!fence.isCurrent(origin))
        throw new Error('Your workspace session changed. Refresh the agent registry.');
      if (!result.ok || !result.data)
        throw new Error(result.error ?? 'Unable to load advisor agents.');
      return result.data;
    },
  });
  if (!fence.enabled)
    return (
      <SettingsSection title="Advisor agents">
        <p className="text-muted-foreground text-xs">
          Agent read permission is required to view the registry.
        </p>
      </SettingsSection>
    );
  if (query.isLoading) return <SettingsLoadingState label="Loading advisor agents…" />;
  if (!query.data || query.isError)
    return (
      <ErrorState
        title="Advisor agents unavailable"
        description={
          query.error instanceof Error
            ? query.error.message
            : 'The agent registry could not be loaded.'
        }
        action={
          <Button size="xs" variant="outline" onClick={() => void query.refetch()}>
            Retry agents
          </Button>
        }
      />
    );
  return (
    <SettingsSection
      title="Advisor agents"
      description="Set registered agents active or paused and adjust their autonomy level. Recommendations remain subject to human approval."
      actions={
        <>
          <span className="text-2xs text-muted-foreground">{query.data.meta.total} registered</span>
          <Button
            size="xs"
            variant="outline"
            disabled={query.isFetching}
            onClick={() => void query.refetch()}
          >
            Refresh agents
          </Button>
        </>
      }
    >
      <div className="grid gap-2.5">
        <SettingsField label="Search agents">
          <input
            type="search"
            className={settingsControlClass}
            value={current.search}
            placeholder="Name or mission"
            onChange={(event) => setFilters({ scope, search: event.target.value, page: 1 })}
          />
        </SettingsField>
        {query.data.items.map((agent) => (
          <AgentControl key={`${scope}:${agent.id}`} agent={agent} />
        ))}
        {query.data.items.length === 0 ? (
          <p className="text-muted-foreground text-xs">
            {current.search
              ? 'No agents match your search.'
              : 'No advisor agents are registered in this workspace.'}
          </p>
        ) : null}
        {query.data.meta.totalPages > 1 ? (
          <div className="flex items-center gap-2">
            <Button
              size="xs"
              variant="outline"
              disabled={current.page <= 1 || query.isFetching}
              onClick={() => setFilters({ ...current, page: current.page - 1 })}
            >
              Previous agents
            </Button>
            <span className="text-muted-foreground text-2xs">
              Page {current.page} of {query.data.meta.totalPages}
            </span>
            <Button
              size="xs"
              variant="outline"
              disabled={current.page >= query.data.meta.totalPages || query.isFetching}
              onClick={() => setFilters({ ...current, page: current.page + 1 })}
            >
              Next agents
            </Button>
          </div>
        ) : null}
      </div>
    </SettingsSection>
  );
}

function AgentControl({ agent }: { agent: AgentSummary }) {
  const auth = useAuth();
  const fence = useSessionScopeFence(PERMISSIONS.AI_AGENTS_UPDATE);
  const queryClient = useQueryClient();
  const draft = useSettingsDraft({
    form: { status: agent.status, autonomyLevel: agent.autonomyLevel },
    version: Date.parse(agent.updatedAt) || 0,
  });
  const [notice, setNotice] = useState<{ scope: string; message: string; error?: Error } | null>(
    null,
  );
  const canUpdate =
    fence.enabled &&
    hasPermission(auth.permissions, PERMISSIONS.AI_AGENTS_UPDATE) &&
    agent.permissions.canUpdate;
  const update = useMutation({
    mutationFn: async ({
      input,
      origin,
    }: {
      input: { status?: 'ACTIVE' | 'PAUSED'; autonomyLevel?: number };
      origin: SessionScopeIdentity;
    }) => {
      if (!fence.isCurrent(origin)) throw new Error('Your workspace session changed.');
      const result = await aiAgentsApi.updateAgent(agent.id, input, {
        sessionOrigin: origin.sessionOrigin,
        allowRefresh: false,
      });
      if (!result.ok || !result.data)
        throw new Error(result.error ?? 'Unable to update advisor agent.');
      return result.data;
    },
  });
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (
      !canUpdate ||
      update.isPending ||
      !draft.form ||
      !draft.baseline ||
      !draft.dirty ||
      draft.changedOnServer
    )
      return;
    const patch = changedSettingsFields(draft.form, draft.baseline, ['status', 'autonomyLevel']);
    if (patch.status && patch.status !== 'ACTIVE' && patch.status !== 'PAUSED') return;
    const origin = fence.identity;
    try {
      const saved = await update.mutateAsync({
        input: {
          ...(patch.status ? { status: patch.status as 'ACTIVE' | 'PAUSED' } : {}),
          ...(patch.autonomyLevel !== undefined ? { autonomyLevel: patch.autonomyLevel } : {}),
        },
        origin,
      });
      if (!fence.isCurrent(origin)) return;
      draft.accept({
        form: { status: saved.status, autonomyLevel: saved.autonomyLevel },
        version: Date.parse(saved.updatedAt) || 0,
      });
      setNotice({ scope: draft.scope, message: `${agent.name} settings saved.` });
      await queryClient.invalidateQueries({ queryKey: aiAgentsQueryKeys.all });
    } catch (error) {
      if (fence.isCurrent(origin))
        setNotice({
          scope: draft.scope,
          message: '',
          error: error instanceof Error ? error : new Error('Agent settings could not be saved.'),
        });
    }
  };
  return (
    <form
      aria-label={`Configure ${agent.name}`}
      className="border-border grid gap-2 border-t pt-2.5"
      onSubmit={(event) => void submit(event)}
    >
      <div>
        <h3 className="text-foreground text-xs font-semibold">{agent.name}</h3>
        <p className="text-muted-foreground text-2xs">{agent.mission}</p>
        <p className="text-muted-foreground text-2xs">
          Current status: {agent.status.toLowerCase()} · {agent.pendingRecommendationCount} awaiting
          approval
        </p>
      </div>
      <SettingsMutationBanner
        message={notice?.scope === draft.scope ? notice.message : null}
        error={notice?.scope === draft.scope ? notice.error : null}
      />
      <SettingsDraftActions
        dirty={draft.dirty}
        changedOnServer={draft.changedOnServer}
        onRevert={() => {
          draft.accept();
          setNotice(null);
        }}
        disabled={update.isPending}
      />
      {draft.form ? (
        <div className="grid items-end gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <SettingsField label={`Status for ${agent.name}`}>
            <select
              className={settingsControlClass}
              value={draft.form.status}
              disabled={!canUpdate || update.isPending}
              onChange={(event) =>
                draft.change({
                  ...draft.form!,
                  status: event.target.value as AgentSummary['status'],
                })
              }
            >
              {draft.form.status !== 'ACTIVE' && draft.form.status !== 'PAUSED' ? (
                <option value={draft.form.status} disabled>
                  {draft.form.status.toLowerCase()}
                </option>
              ) : null}
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
            </select>
          </SettingsField>
          <SettingsField label={`Autonomy level for ${agent.name}`}>
            <select
              className={settingsControlClass}
              value={draft.form.autonomyLevel}
              disabled={!canUpdate || update.isPending}
              onChange={(event) =>
                draft.change({ ...draft.form!, autonomyLevel: Number(event.target.value) })
              }
            >
              {[0, 1, 2, 3, 4, 5].map((level) => (
                <option key={level} value={level}>
                  Level {level}
                </option>
              ))}
            </select>
          </SettingsField>
          {canUpdate ? (
            <Button
              type="submit"
              size="xs"
              disabled={update.isPending || !draft.dirty || draft.changedOnServer}
            >
              {update.isPending ? 'Saving…' : 'Save agent'}
            </Button>
          ) : (
            <span className="text-muted-foreground text-2xs">Read only</span>
          )}
        </div>
      ) : null}
    </form>
  );
}
